import { useEffect, useRef, useCallback } from "react";
import { useTranslation, BASE_LANG } from "@/contexts/TranslationContext";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "SVG", "PATH", "CODE", "PRE", "TEXTAREA", "INPUT", "SELECT"]);
const MIN_TEXT_LENGTH = 2;
const BATCH_SIZE = 80;
const ATTR_ORIGINAL = "data-original-text";
const ATTR_LANG = "data-translated-lang";

function getTextNodes(root: Node): Text[] {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      if (parent.closest("[data-no-translate]")) return NodeFilter.FILTER_REJECT;
      if (parent.hasAttribute("data-no-translate")) return NodeFilter.FILTER_REJECT;
      const text = node.textContent?.trim();
      if (!text || text.length < MIN_TEXT_LENGTH) return NodeFilter.FILTER_REJECT;
      if (/^[\d\s+\-()./:@#%&=?_,;]+$/.test(text)) return NodeFilter.FILTER_REJECT;
      if (/^https?:\/\//.test(text)) return NodeFilter.FILTER_REJECT;
      if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(text)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);
  return nodes;
}

export function useAutoTranslate() {
  const { currentLang, translateBatch, isTranslating } = useTranslation();
  const translatingRef = useRef(false);
  const prevLangRef = useRef(currentLang);
  const mutationPausedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const restoreOriginals = useCallback(() => {
    mutationPausedRef.current = true;
    document.querySelectorAll(`[${ATTR_ORIGINAL}]`).forEach((el) => {
      const original = el.getAttribute(ATTR_ORIGINAL);
      if (original && el.firstChild) {
        el.firstChild.textContent = original;
        el.removeAttribute(ATTR_ORIGINAL);
        el.removeAttribute(ATTR_LANG);
      }
    });
    setTimeout(() => { mutationPausedRef.current = false; }, 100);
  }, []);

  const translatePage = useCallback(async (targetLang: string) => {
    if (targetLang === BASE_LANG || translatingRef.current) return;
    
    // Abort any previous in-flight translation
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    
    translatingRef.current = true;
    mutationPausedRef.current = true;

    try {
      const textNodes = getTextNodes(document.body);
      const textToNodes = new Map<string, { node: Text; parent: HTMLElement }[]>();

      for (const node of textNodes) {
        const parent = node.parentElement;
        if (!parent) continue;
        // Skip if already translated to this exact language
        if (parent.getAttribute(ATTR_LANG) === targetLang) continue;

        const original = parent.getAttribute(ATTR_ORIGINAL) || node.textContent?.trim() || "";
        if (!original) continue;

        if (!parent.hasAttribute(ATTR_ORIGINAL)) {
          parent.setAttribute(ATTR_ORIGINAL, original);
        }

        if (!textToNodes.has(original)) textToNodes.set(original, []);
        textToNodes.get(original)!.push({ node, parent });
      }

      const uniqueTexts = Array.from(textToNodes.keys());
      if (uniqueTexts.length === 0) return;

      for (let i = 0; i < uniqueTexts.length; i += BATCH_SIZE) {
        // Check if aborted (user switched language mid-translation)
        if (controller.signal.aborted) return;
        
        const batch = uniqueTexts.slice(i, i + BATCH_SIZE);
        const translations = await translateBatch(batch);

        if (controller.signal.aborted) return;

        batch.forEach((original, idx) => {
          const translated = translations[idx];
          if (translated && translated !== original) {
            const entries = textToNodes.get(original) || [];
            for (const { node, parent } of entries) {
              const fullOriginal = node.textContent || "";
              const leadingSpace = fullOriginal.match(/^\s*/)?.[0] || "";
              const trailingSpace = fullOriginal.match(/\s*$/)?.[0] || "";
              node.textContent = leadingSpace + translated + trailingSpace;
              parent.setAttribute(ATTR_LANG, targetLang);
            }
          }
        });
      }
    } finally {
      translatingRef.current = false;
      setTimeout(() => { mutationPausedRef.current = false; }, 200);
    }
  }, [translateBatch]);

  useEffect(() => {
    if (currentLang === BASE_LANG) {
      abortRef.current?.abort();
      restoreOriginals();
      prevLangRef.current = currentLang;
      return;
    }

    // Always restore originals first when language changes, then translate fresh
    if (prevLangRef.current !== currentLang) {
      abortRef.current?.abort();
      restoreOriginals();
      prevLangRef.current = currentLang;
    }

    // Wait for DOM to settle after restore before translating
    const timer = setTimeout(() => {
      translatePage(currentLang);
    }, 350);

    return () => clearTimeout(timer);
  }, [currentLang, translatePage, restoreOriginals]);

  // Observe DOM mutations but pause during our own translations
  useEffect(() => {
    if (currentLang === BASE_LANG) return;

    let debounceTimer: ReturnType<typeof setTimeout>;
    const observer = new MutationObserver(() => {
      if (mutationPausedRef.current || translatingRef.current) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        translatePage(currentLang);
      }, 800);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, [currentLang, translatePage]);
}
